import { useEffect, type ReactNode } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, isFirebaseConfigured } from "@/lib/firebase";
import { fetchUser } from "@/lib/queries";
import { useAuthStore } from "@/store/auth";
import { toast } from "sonner";

export function AuthProvider({ children }: { children: ReactNode }) {
  const setUser = useAuthStore((s) => s.setUser);
  const reset = useAuthStore((s) => s.reset);
  const setLoading = useAuthStore((s) => s.setLoading);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      reset();
      return;
    }

    setLoading(true);

    const unsub = onAuthStateChanged(
      auth,
      async (fbUser) => {
        if (!fbUser) {
          reset();
          return;
        }

        try {
          const profile = await fetchUser(fbUser.uid);

          if (!profile) {
            await signOut(auth);
            reset();
            toast.error("User profile not found.");
            return;
          }

          if (profile.role !== "admin") {
            await signOut(auth);
            reset();
            toast.error("Access denied. Admin privileges required.");
            return;
          }

          if (profile.status !== "active") {
            await signOut(auth);
            reset();
            toast.error("Your account has been deactivated. Contact support.");
            return;
          }

          setUser(fbUser.uid, profile);
        } catch (err) {
          console.error("Auth verification failed:", err);
          await signOut(auth).catch(() => undefined);
          reset();
          toast.error("Could not verify your account. Please try again.");
        }
      },
      (error) => {
        console.error("Auth state change error:", error);
        reset();
        toast.error("Authentication error occurred.");
      },
    );

    return () => unsub();
  }, [reset, setUser, setLoading]);

  return <>{children}</>;
}
